
'use client';

import type { Dispatch, SetStateAction } from 'react';
import React, { createContext, useState, useEffect, useContext } from 'react';

export type AppMode = 'debug' | 'exam';

interface ModeContextType {
  mode: AppMode;
  setMode: Dispatch<SetStateAction<AppMode>>;
  isLoadingMode: boolean;
}

export const ModeContext = createContext<ModeContextType | undefined>(undefined);

export const ModeProvider = ({ children }: { children: React.ReactNode }) => {
  const [mode, setMode] = useState<AppMode>('exam'); // Default to exam
  const [isLoadingMode, setIsLoadingMode] = useState(true);

  useEffect(() => {
    try {
      const storedMode = localStorage.getItem('appMode') as AppMode | null;
      if (storedMode && (storedMode === 'debug' || storedMode === 'exam')) {
        setMode(storedMode);
      }
    } catch (error) {
      // localStorage might not be available (e.g. SSR, private browsing)
      // Default to 'debug'
    }
    setIsLoadingMode(false);
  }, []);

  useEffect(() => {
    if (!isLoadingMode) {
      try {
        localStorage.setItem('appMode', mode);
      } catch (error) {
        // localStorage might not be available
      }
    }
  }, [mode, isLoadingMode]);

  return (
    <ModeContext.Provider value={{ mode, setMode, isLoadingMode }}>
      {children}
    </ModeContext.Provider>
  );
};

export const useMode = () => {
  const context = useContext(ModeContext);
  if (context === undefined) {
    throw new Error('useMode must be used within a ModeProvider');
  }
  return context;
};
